import React, { useState } from 'react';
import { FaBed, FaCouch, FaTimes, FaUtensils } from 'react-icons/fa';
import api from '../utils/api';
import './RepairService.css';

const RepairService = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    furnitureType: '',
    issues: [],
    photos: [],
    dimensions: { width: '', height: '', depth: '' },
    additionalDetails: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    zipCode: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [pincodeError, setPincodeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const ALLOWED_PINCODE = '202001';

  const furnitureTypes = [
    { id: 'sofa', label: 'Sofa', icon: <FaCouch /> },
    { id: 'bed', label: 'Bed', icon: <FaBed /> },
    { id: 'dining', label: 'Dining', icon: <FaUtensils /> }
  ];

  const issueTypes = [
    { id: 'fabric-tear', label: 'Fabric Tear/Stains' },
    { id: 'sagging', label: 'Sagging Cushions' },
    { id: 'broken-frame', label: 'Broken Frame/Leg' },
    { id: 'reupholstery', label: 'Re-upholstery Needed' },
    { id: 'springs', label: 'Spring Repair' },
    { id: 'other', label: 'Other' }
  ];

  const handleFurnitureSelect = (type) => {
    setFormData({ ...formData, furnitureType: type });
  };

  const handleIssueToggle = (issue) => {
    const issues = formData.issues.includes(issue)
      ? formData.issues.filter(i => i !== issue)
      : [...formData.issues, issue];
    setFormData({ ...formData, issues });
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData({ ...formData, photos: [...formData.photos, ...files] });
  };

  const removePhoto = (index) => {
    const photos = formData.photos.filter((_, i) => i !== index);
    setFormData({ ...formData, photos });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle phone number with +91 prefix
    if (name === 'phone') {
      let phoneValue = value.replace(/[^0-9+]/g, ''); // Allow only numbers and +
      
      // If user starts typing and doesn't include +91, add it
      if (phoneValue && !phoneValue.startsWith('+91')) {
        // Remove any existing + at the start
        phoneValue = phoneValue.replace(/^\+/, '');
        phoneValue = '+91' + phoneValue;
      }
      
      // Limit to +91 + 10 digits (13 characters total)
      if (phoneValue.length > 13) {
        phoneValue = phoneValue.substring(0, 13);
      }
      
      setFormData({ ...formData, phone: phoneValue });
      return;
    }
    
    // Validate pincode
    if (name === 'zipCode') {
      if (value && value !== ALLOWED_PINCODE) {
        setPincodeError(`We currently accept repair requests in pincode ${ALLOWED_PINCODE} only.`);
      } else {
        setPincodeError('');
      }
    }
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: { ...formData[parent], [child]: value }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Convert File objects to base64
      const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      };

      const photoBase64Array = await Promise.all(
        formData.photos.map(async (photo) => {
          if (photo instanceof File) {
            return await convertToBase64(photo);
          }
          return photo; // Already a URL/string
        })
      );

      const repairData = {
        furnitureType: formData.furnitureType,
        issues: formData.issues,
        photos: photoBase64Array,
        dimensions: formData.dimensions,
        additionalDetails: formData.additionalDetails,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        zipCode: formData.zipCode,
      };

      const response = await api.post('/repairs', repairData);
      
      if (response.data.success) {
        console.log('Repair request submitted successfully:', response.data);
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Error submitting repair request:', err);
      setError(err.response?.data?.message || 'Failed to submit repair request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.furnitureType !== '';
      case 2:
        return formData.issues.length > 0;
      case 3:
        return formData.photos.length > 0;
      case 4:
        return true;
      case 5:
        return formData.name && formData.phone && formData.address && formData.zipCode && formData.zipCode === ALLOWED_PINCODE;
      default:
        return false;
    }
  };

  if (submitted) {
    return (
      <div className="repair-service">
        <div className="container">
          <div className="success-screen">
            <div className="success-icon">✓</div>
            <h1>We're on it!</h1>
            <p>Your repair request has been confirmed and submitted successfully.</p>
            <p className="email-sent">We've sent a confirmation to {formData.email} and WhatsApp at {formData.phone}</p>
            <p>Expect a personalized estimate within 24 hours.</p>
            <button onClick={() => window.location.href = '/'} className="btn btn-primary">
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="repair-service">
      <div className="container">
        <div className="repair-header">
          <h1>Sofa Rescue</h1>
          <p>Let's bring your furniture back to life</p>
        </div>

        <div className="repair-wizard">
          <div className="wizard-progress">
            {[1, 2, 3, 4, 5].map(s => (
              <div key={s} className={`progress-step ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`}>
                <span>{s}</span>
              </div>
            ))}
          </div>

          <div className="wizard-content">
            {step === 1 && (
              <div className="wizard-step">
                <h2>What needs fixing?</h2>
                <div className="furniture-types">
                  {furnitureTypes.map(type => (
                    <div
                      key={type.id}
                      className={`furniture-card ${formData.furnitureType === type.id ? 'selected' : ''}`}
                      onClick={() => handleFurnitureSelect(type.id)}
                    >
                      <div className="furniture-icon">{type.icon}</div>
                      <span>{type.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="wizard-step">
                <h2>What's wrong?</h2>
                <p className="step-description">Select all that apply</p>
                <div className="issue-list">
                  {issueTypes.map(issue => (
                    <label key={issue.id} className="issue-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.issues.includes(issue.id)}
                        onChange={() => handleIssueToggle(issue.id)}
                      />
                      <span className="checkbox-custom"></span>
                      <span className="issue-label">{issue.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="wizard-step">
                <h2>Show us the damage</h2>
                <p className="step-description">Upload photos (wide shot and close-ups)</p>
                
                <div className="photo-upload">
                  <label className="upload-area">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      style={{ display: 'none' }}
                    />
                    <div className="upload-prompt">
                      <span className="upload-icon">📸</span>
                      <p>Click or drag photos here</p>
                      <span className="upload-hint">JPG, PNG up to 10MB each</span>
                    </div>
                  </label>

                  {formData.photos.length > 0 && (
                    <div className="photo-preview-grid">
                      {formData.photos.map((photo, index) => (
                        <div key={index} className="photo-preview">
                          <img src={URL.createObjectURL(photo)} alt={`Preview ${index + 1}`} />
                          <button
                            type="button"
                            className="remove-photo"
                            onClick={() => removePhoto(index)}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="wizard-step">
                <h2>Dimensions & Details</h2>
                <p className="step-description">Optional but helps us estimate fabric needs</p>
                
                <div className="dimensions-grid">
                  <div className="form-group">
                    <label>Width (inches)</label>
                    <input
                      type="number"
                      name="dimensions.width"
                      value={formData.dimensions.width}
                      onChange={handleInputChange}
                      placeholder="72"
                    />
                  </div>
                  <div className="form-group">
                    <label>Height (inches)</label>
                    <input
                      type="number"
                      name="dimensions.height"
                      value={formData.dimensions.height}
                      onChange={handleInputChange}
                      placeholder="36"
                    />
                  </div>
                  <div className="form-group">
                    <label>Depth (inches)</label>
                    <input
                      type="number"
                      name="dimensions.depth"
                      value={formData.dimensions.depth}
                      onChange={handleInputChange}
                      placeholder="40"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Any extra details?</label>
                  <textarea
                    name="additionalDetails"
                    value={formData.additionalDetails}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Tell us more about the issue or any specific concerns..."
                  />
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="wizard-step">
                <h2>Where should we send the quote?</h2>
                
                <div className="contact-form">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91XXXXXXXXXX"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Enter full address"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Zip Code *</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      placeholder="Enter 202001"
                      required
                      className={pincodeError ? 'error' : ''}
                    />
                    {pincodeError && (
                      <div className="error-message">
                        {pincodeError}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="error-banner" style={{
              padding: '15px',
              margin: '20px 0',
              backgroundColor: '#fee',
              color: '#c33',
              borderRadius: '8px',
              border: '1px solid #fcc'
            }}>
              {error}
            </div>
          )}

          <div className="wizard-actions">
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="btn btn-secondary"
              >
                Back
              </button>
            )}
            
            {step < 5 ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                className="btn btn-primary"
                disabled={!canProceed()}
              >
                Next Step
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-primary"
                disabled={!canProceed() || loading}
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairService;